import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { TabInterface } from '../../../../../interfaces/tabs/TabInterface';
import TabsHeader from './TabsHeader/TabsHeader';

export interface TabsProps extends JSX.ElementChildrenAttribute {
  isVertical?: boolean;
  contentAfterHeader?: JSX.Element;
  manualActiveTabId?: string;
}

const ServicesTabs = ({ children, contentAfterHeader, manualActiveTabId }: TabsProps) => {
  const [tabs, setTabs] = useState<Array<{ label: string; id: string }>>([]);
  const [content, setContent] = useState<ReactNode>();
  const [activeTab, setActiveTab] = useState<string>();

  const findActiveIndex = () => {
    return tabs.findIndex(item => activeTab === item.id);
  };

  const nextTab = () => {
    const index = findActiveIndex();
    if (index + 1 === tabs.length) {
      setActiveTab(tabs[0].id);
      return;
    }
    setActiveTab(tabs[index + 1].id);
  };

  const prevTab = () => {
    const index = findActiveIndex();
    if (index === 0) {
      setActiveTab(tabs[tabs.length - 1].id);
      return;
    }
    setActiveTab(tabs[index - 1].id);
  };

  useEffect(() => {
    const labels = React.Children.map(children, (child, idx) => {
      if (React.isValidElement<TabInterface>(child)) {
        if (idx === 0 && !activeTab) {
          setActiveTab(child.props.id);
        }
        return { label: child.props.label, id: child.props.id };
      }
    });
    setTabs(labels);
  }, [activeTab, children]);

  useEffect(() => {
    React.Children.forEach(children, child => {
      if (React.isValidElement<TabInterface>(child) && child.props.id === activeTab) {
        setContent(child.props.children);
      }
    });
  }, [children, activeTab]);

  useEffect(() => {
    if (manualActiveTabId) {
      setActiveTab(manualActiveTabId);
    }
  }, [manualActiveTabId]);

  const changeTab = useCallback((tab: string) => setActiveTab(tab), []);

  return (
    <div id='servicesTab'>
      <TabsHeader
        items={tabs}
        changeTab={changeTab}
        activeTabId={activeTab}
        navigation={{ nextTab, prevTab }}
        currentIndex={findActiveIndex() + 1}
      />
      {contentAfterHeader}
      <div>{content}</div>
    </div>
  );
};

export default ServicesTabs;
